import { MapPin } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import Avatar from '../../../shared/components/Avatar';
import Modal from '../../../shared/components/Modal';
import { displayCommunityName } from '../../../shared/utils/user';
import { getVisitedPlaces } from '../helpers/getVisitedPlaces';
import { getCommunityProfile } from '../services/communityProfileService';

type UserProfileModalProps = {
  reviewer: Record<string, any>;
  places: Array<Record<string, any> & { id: string }>;
  onClose: () => void;
  onOpenPlace: (place: Record<string, any>) => void;
  t: any;
};

export default function UserProfileModal({
  reviewer,
  places,
  onClose,
  onOpenPlace,
  t,
}: UserProfileModalProps) {
  const [profile, setProfile] = useState<Record<string, any>>({
    id: reviewer.authorId,
    username: reviewer.authorUsername || '',
    displayName: displayCommunityName(reviewer.authorName) || reviewer.authorUsername || '',
    avatar: reviewer.authorAvatar || '',
    color: reviewer.authorColor || '',
    visited: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getCommunityProfile(reviewer.authorId)
      .then((nextProfile) => {
        if (active && nextProfile) setProfile(nextProfile);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [reviewer.authorId]);

  const visitedPlaces = useMemo(
    () => getVisitedPlaces(profile.visited, places),
    [places, profile.visited],
  );

  return (
    <Modal onClose={onClose} label={t.communityProfile}>
      <div className="community-profile-modal">
        <div className="community-profile-heading">
          <Avatar user={profile} size={72} />
          <div>
            <span className="eyebrow coral">{t.communityProfile}</span>
            <h2>{profile.displayName || profile.username}</h2>
            <p>@{profile.username}</p>
          </div>
        </div>
        <section className="community-visited-section">
          <div>
            <h3>{t.visitedPlaces}</h3>
            <span>{visitedPlaces.length}</span>
          </div>
          <div className="community-visited-list">
            {loading && <p className="community-profile-state">{t.loading}</p>}
            {!loading && !visitedPlaces.length && (
              <p className="community-profile-state">{t.noVisitedPlaces}</p>
            )}
            {visitedPlaces.map((place) => (
              <button type="button" key={place.id} onClick={() => onOpenPlace(place)}>
                <img src={place.image || '/place-placeholder.svg'} alt="" />
                <span>
                  <b>{place.name}</b>
                  <small><MapPin weight="fill" />{place.address}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </Modal>
  );
}
